<script setup lang="ts">
import MList from "../MList";
import MItemLabel from "../MItemLabel";
import MItemSection from "../MItemSection";
import { nextTick, onMounted, onUpdated, Ref, ref, watch } from "vue";
import { CanvasImage, VariableOption } from "./types";
import { openModal } from "../../../composables/dialog/drawer";
import MMenu from "../MMenu";
import MItem from "../MItem";
import { escapeRegex, sortByStartsWith } from "../../../utils/misc";
import { CursorValues, Direction, TextAreaKeyboardEvent } from "../MInlineTextEntryInput/types";
import { Key } from "ts-key-enum";
import { listen } from "../../../utils/bus";

const props = defineProps<{
  width: number;
  height: number;
  /** The initial images to draw on the canvas */
  initialImages?: [];
  /** Will make the position absolute. Required for drawing/highlighting on PDFs */
  isAbsolute?: false;
  /** The ref of the element that the canvas is overlaying. Required for drawing/highlighting on PDFs */
  overlayRef?: String;
  variableOptions: [];
  actionModalUrl: string;
  disableDrawing?: false;
  /** Uniform inset (px) inside which boxes are not allowed to be dragged. */
  margin?: number;
  /**
   * Drag mode:
   * - "draw" (default): dragging in empty space creates a new variable box.
   * - "select": dragging in empty space paints a translucent marquee; on
   *   mouseup, every existing box intersecting the marquee is added to the
   *   selection.
   * Either way, dragging on an existing box still moves it.
   */
  mode?: "draw" | "select";
}>();

const emit = defineEmits({
  blur: (_inputValue: string) => true,
  enterKeydown: (_cursorValues: CursorValues) => true,
  "update:images": (_images: CanvasImage[]) => true,
  // Array-shaped from day one so the parent doesn't need to change when
  // multi-select lands; for now it's always length 0 or 1.
  "select:images": (_images: CanvasImage[]) => true,
});

// Selection state lives on the canvas so highlight rendering stays local.
// Parent consumes selection via the `select:images` emit.
const selectedIndices = ref<Set<number>>(new Set());

function emitSelection() {
  const selected: CanvasImage[] = [];
  selectedIndices.value.forEach((idx) => {
    const img = images.value[idx];
    if (img) selected.push(img);
  });
  emit("select:images", selected);
}

function clearSelection() {
  if (selectedIndices.value.size === 0) return;
  selectedIndices.value = new Set();
  emitSelection();
  redraw(false);
}

function getSelectedImages(): CanvasImage[] {
  const out: CanvasImage[] = [];
  selectedIndices.value.forEach((idx) => {
    const img = images.value[idx];
    if (img) out.push(img);
  });
  return out;
}

defineExpose({ clearSelection, getSelectedImages });

const drawingCanvas = ref<HTMLCanvasElement | null>(null);
const images = ref<CanvasImage[]>([]);
const context = ref<CanvasRenderingContext2D | null>(null);

const inputValue = ref<string>("");
const textAreaEl = ref<InstanceType<typeof HTMLTextAreaElement> | null>(null);

let drawing = false;
let startingPositionX: number = null;
let startingPositionY: number = null;
// ENG-20693 Marquee-select state. Active only when `mode === "select"` and the
// drag starts in empty canvas space.
let marqueeActive = false;
let marqueeEndX: number = null;
let marqueeEndY: number = null;
// ENG-20436 reactive so v-if re-evaluates
const stoppedDrawing = ref(false);
// ENG-20436 explicit menu visibility — typing/arrows open it, picks/Esc close it
const menuOpen = ref(false);
let currentImageIndex = null;

// Drag state for moving an existing image. A drag is only committed once the
// pointer moves past DRAG_THRESHOLD_PX; otherwise mouseup is treated as a
// click and only selection is updated.
//
// dragIndices/dragOriginals are the *snapshot* of what's being dragged: if
// the box under the cursor is part of the current selection, every selected
// box drags together (group move); otherwise only that single box moves.
const DRAG_THRESHOLD_PX = 3;
let dragCandidateIndex: number | null = null;
let dragStartX = 0;
let dragStartY = 0;
let dragIndices: number[] = [];
let dragOriginals: { x: number; y: number }[] = [];
let dragging = false;

// Resize state — only active for the single-selection case. The handle
// names follow CSS compass conventions (n/s/e/w + corners).
type ResizeHandle = "nw" | "n" | "ne" | "e" | "se" | "s" | "sw" | "w";
const RESIZE_HANDLE_PX = 6;
let resizeBoxIndex: number | null = null;
let resizeHandle: ResizeHandle | null = null;
let resizeOriginal: {
  coordX: number;
  coordY: number;
  width: number;
  height: number;
} | null = null;

const menuDisplayVariables = ref(false);
const filteredActions = ref<Array<VariableOption>>(props.variableOptions ?? []);
const refActionMenuItems = ref<InstanceType<typeof MItem>[]>([]);
const focusedActionIndex = ref(0);
const MENU_WIDTH = 300;
const MENU_MAX_HEIGHT = 300;

const textLeftPosition = ref<string>("-100px");
const canvasTextAreaDisplay = ref<string>("none");
const textTopPosition = ref<string>("-100px");

onMounted(() => {
  if (props.initialImages) {
    images.value = props.initialImages;
  }

  setContext();
  nextTick(() => {
    drawInitialImage();
  });
});

onUpdated(() => {
  // This creates a clear rectangle across the canvas to remove the old rectangles
  const rect: CanvasRenderingContext2D = drawingCanvas.value.getContext("2d");
  rect.clearRect(0, 0, drawingCanvas.value.width, drawingCanvas.value.height);

  // upon clearing update the images and draw on fresh canvas
  images.value = props.initialImages;

  // Drop any selection indices that no longer point at a valid image (handles
  // deletions / smaller pages). Drag commits preserve length+order so survive.
  let droppedAny = false;
  selectedIndices.value.forEach((idx) => {
    if (idx >= images.value.length) {
      selectedIndices.value.delete(idx);
      droppedAny = true;
    }
  });
  if (droppedAny) emitSelection();

  redraw(false);
});

watch(
  inputValue,
  (newValue, _) => {
    // Update menu visibility
    menuDisplayVariables.value =
      props.variableOptions !== undefined && props.variableOptions?.length > 0;

    if (menuDisplayVariables.value === true && props.variableOptions) {
      stoppedDrawing.value = true;
      menuOpen.value = true;
      // Filter actions list with the latest text value (minus the forward slash)
      const searchText = newValue.toLowerCase();
      focusedActionIndex.value = 0;

      if (searchText.length === 0) {
        filteredActions.value = props.variableOptions;
      } else {
        const regex = new RegExp(`\\b${escapeRegex(searchText)}`, "i");
        const startsWithRegex = new RegExp(`^${escapeRegex(searchText)}`, "i");
        filteredActions.value = props.variableOptions
          .filter((v) => regex.test(v.value) || regex.test(v.label))
          .sort((a, b) => sortByStartsWith(a.label, b.label, startsWithRegex));
      }
    }
  },
  { immediate: true },
);

listen("redrawCanvas", () => {
  requestAnimationFrame(() => {
    images.value = props.initialImages;
    redraw(false);
  });
});

function setContext() {
  context.value = context.value ? context.value : drawingCanvas.value.getContext("2d");
}

// Clamp a cursor point to the margin-inset draw region so new boxes are
// kept inside the page bounds (matches the drag clamp).
function clampToBounds(x: number, y: number): { x: number; y: number } {
  const margin = props.margin ?? 0;
  return {
    x: Math.min(Math.max(x, margin), props.width - margin),
    y: Math.min(Math.max(y, margin), props.height - margin),
  };
}

// Returns which resize handle (if any) the cursor is currently on for the
// given box. Corner checks run before edge checks so the corner wins in
// the overlap region.
function getHandleAt(img: CanvasImage, x: number, y: number): ResizeHandle | null {
  const left = img.coordX;
  const top = img.coordY;
  const right = img.coordX + img.width;
  const bottom = img.coordY + img.height;
  const t = RESIZE_HANDLE_PX;
  if (Math.abs(x - left) <= t && Math.abs(y - top) <= t) return "nw";
  if (Math.abs(x - right) <= t && Math.abs(y - top) <= t) return "ne";
  if (Math.abs(x - left) <= t && Math.abs(y - bottom) <= t) return "sw";
  if (Math.abs(x - right) <= t && Math.abs(y - bottom) <= t) return "se";
  if (Math.abs(y - top) <= t && x >= left && x <= right) return "n";
  if (Math.abs(y - bottom) <= t && x >= left && x <= right) return "s";
  if (Math.abs(x - left) <= t && y >= top && y <= bottom) return "w";
  if (Math.abs(x - right) <= t && y >= top && y <= bottom) return "e";
  return null;
}

function handleToCursor(h: ResizeHandle): string {
  if (h === "n" || h === "s") return "ns-resize";
  if (h === "e" || h === "w") return "ew-resize";
  if (h === "ne" || h === "sw") return "nesw-resize";
  return "nwse-resize"; // nw | se
}

function singleSelectedImage(): { idx: number; img: CanvasImage } | null {
  if (selectedIndices.value.size !== 1) return null;
  const idx = selectedIndices.value.values().next().value as number;
  const img = images.value[idx];
  return img ? { idx, img } : null;
}

// Double-click on a saved box opens its create-variable / rename modal.
// (Single click is reserved for selection now; rename has its own gesture.)
function handleDblClick(event: MouseEvent) {
  if (props.disableDrawing) return;
  for (let idx = 0; idx < images.value.length; idx++) {
    const image = images.value[idx];
    if (
      image.coordX <= event.offsetX &&
      image.coordX + image.width >= event.offsetX &&
      image.coordY <= event.offsetY &&
      image.coordY + image.height >= event.offsetY &&
      image.id
    ) {
      openModal(props.actionModalUrl, { params: { id: image.id } });
      return;
    }
  }
}

function drawInitialImage() {
  redraw(false);
}

function startDraw(event: MouseEvent) {
  if (drawing || dragging || dragCandidateIndex !== null || props.disableDrawing) {
    return;
  }
  // Drop input focus so Cmd/Ctrl+Z routes to the parent's keydown handler
  // (otherwise an active text/number field swallows it as native undo).
  const active = document.activeElement as HTMLElement | null;
  if (active && (active.tagName === "INPUT" || active.tagName === "TEXTAREA")) {
    active.blur();
  }
  if (currentImageIndex) {
    tidyImageAndInput();
  }

  // Resize takes priority over hit-test/draw — only checked on the single
  // selected box so plain clicks on unselected boxes still select normally.
  const sel = singleSelectedImage();
  if (sel) {
    const handle = getHandleAt(sel.img, event.offsetX, event.offsetY);
    if (handle) {
      resizeBoxIndex = sel.idx;
      resizeHandle = handle;
      resizeOriginal = {
        coordX: sel.img.coordX,
        coordY: sel.img.coordY,
        width: sel.img.width,
        height: sel.img.height,
      };
      return;
    }
  }

  let hitIndex = -1;
  images.value.forEach((image: CanvasImage, idx: number) => {
    if (
      image.coordX <= event.offsetX &&
      image.coordX + image.width >= event.offsetX &&
      image.coordY <= event.offsetY &&
      image.coordY + image.height >= event.offsetY
    ) {
      hitIndex = idx;
    }
  });

  if (hitIndex === -1) {
    // Empty canvas area. In "draw" mode start a new rectangle; in "select"
    // mode start a marquee (we don't clear the existing selection so users
    // can extend it by sweeping over more boxes).
    const start = clampToBounds(event.offsetX, event.offsetY);
    startingPositionX = start.x;
    startingPositionY = start.y;
    if (props.mode === "select") {
      marqueeActive = true;
      marqueeEndX = start.x;
      marqueeEndY = start.y;
    } else {
      clearSelection();
      drawing = true;
      stoppedDrawing.value = false;
    }
    return;
  }

  // Inside an existing rectangle — update selection, then set up a drag
  // candidate. mousemove decides whether this becomes a drag or stays a
  // click (handled in stopDraw).
  //
  // Selection semantics:
  // - Shift-click toggles the box in/out of the current selection.
  // - Plain click on an unselected box replaces the selection with just it.
  // - Plain click on an already-selected box keeps the selection intact, so
  //   the user can drag the group without first re-selecting.
  if (event.shiftKey) {
    const next = new Set(selectedIndices.value);
    if (next.has(hitIndex)) next.delete(hitIndex);
    else next.add(hitIndex);
    selectedIndices.value = next;
  } else if (!selectedIndices.value.has(hitIndex)) {
    selectedIndices.value = new Set([hitIndex]);
  }
  emitSelection();

  dragCandidateIndex = hitIndex;
  dragStartX = event.offsetX;
  dragStartY = event.offsetY;

  // If the hit box is in the current selection, every selected box drags
  // together. Otherwise only the hit box moves. Snapshot starting coords
  // for each so mousemove just adds (dx, dy) to the originals.
  dragIndices = selectedIndices.value.has(hitIndex)
    ? Array.from(selectedIndices.value)
    : [hitIndex];
  dragOriginals = dragIndices.map((idx) => ({
    x: images.value[idx].coordX,
    y: images.value[idx].coordY,
  }));

  dragging = false;
  redraw(false);
}

function draw(event: MouseEvent) {
  if (props.disableDrawing) return;

  // Active resize — mutate the resizing box based on the handle and the
  // clamped cursor position. Opposite-edge collapse is prevented so the
  // box never inverts (width/height clamp to 0).
  if (resizeHandle && resizeBoxIndex !== null && resizeOriginal) {
    const img = images.value[resizeBoxIndex];
    if (!img || !drawingCanvas.value) return;
    const c = clampToBounds(event.offsetX, event.offsetY);
    let left = resizeOriginal.coordX;
    let top = resizeOriginal.coordY;
    let right = resizeOriginal.coordX + resizeOriginal.width;
    let bottom = resizeOriginal.coordY + resizeOriginal.height;
    if (resizeHandle.includes("n")) top = Math.min(c.y, bottom);
    if (resizeHandle.includes("s")) bottom = Math.max(c.y, top);
    if (resizeHandle.includes("w")) left = Math.min(c.x, right);
    if (resizeHandle.includes("e")) right = Math.max(c.x, left);
    img.coordX = left;
    img.coordY = top;
    img.width = right - left;
    img.height = bottom - top;
    drawingCanvas.value
      .getContext("2d")
      .clearRect(0, 0, drawingCanvas.value.width, drawingCanvas.value.height);
    redraw(false);
    return;
  }

  // Idle hover — update the cursor when over a resize handle of the
  // single selected box. Skip while drawing/dragging since those set
  // their own implicit cursor through pointer state.
  if (!drawing && dragCandidateIndex === null && drawingCanvas.value) {
    let cursor = "default";
    const sel = singleSelectedImage();
    if (sel) {
      const handle = getHandleAt(sel.img, event.offsetX, event.offsetY);
      if (handle) cursor = handleToCursor(handle);
    }
    if (drawingCanvas.value.style.cursor !== cursor) {
      drawingCanvas.value.style.cursor = cursor;
    }
  }

  if (drawing) {
    if (!context.value) {
      this.setContext();
    }

    const end = clampToBounds(event.offsetX, event.offsetY);
    context.value.beginPath();
    context.value.rect(
      startingPositionX,
      startingPositionY,
      end.x - startingPositionX,
      end.y - startingPositionY,
    );
    context.value.closePath();
    context.value.fillRect(0, 0, window.innerWidth, window.innerHeight);
    context.value.clearRect(0, 0, window.innerWidth, window.innerHeight);
    context.value.fillStyle = "rgba(255, 179, 71, 0.8)";
    context.value.lineWidth = 3;
    context.value.fill();

    redraw(false);
    return;
  }

  // ENG-20693 Marquee preview — translucent blue rectangle, distinct from the
  // orange new-box preview.
  if (marqueeActive && drawingCanvas.value) {
    const end = clampToBounds(event.offsetX, event.offsetY);
    marqueeEndX = end.x;
    marqueeEndY = end.y;
    const ctx = drawingCanvas.value.getContext("2d");
    ctx.clearRect(0, 0, drawingCanvas.value.width, drawingCanvas.value.height);
    redraw(false);
    const x = Math.min(startingPositionX, marqueeEndX);
    const y = Math.min(startingPositionY, marqueeEndY);
    const w = Math.abs(marqueeEndX - startingPositionX);
    const h = Math.abs(marqueeEndY - startingPositionY);
    ctx.save();
    ctx.fillStyle = "rgba(0, 102, 255, 0.12)";
    ctx.strokeStyle = "#0066ff";
    ctx.lineWidth = 1;
    ctx.setLineDash([4, 4]);
    ctx.fillRect(x, y, w, h);
    ctx.strokeRect(x, y, w, h);
    ctx.restore();
    return;
  }

  if (dragCandidateIndex !== null) {
    const dx = event.offsetX - dragStartX;
    const dy = event.offsetY - dragStartY;
    if (!dragging && Math.abs(dx) <= DRAG_THRESHOLD_PX && Math.abs(dy) <= DRAG_THRESHOLD_PX) {
      // Still within the click tolerance — wait for more movement.
      return;
    }
    dragging = true;
    if (!drawingCanvas.value) return;

    // Clamp the proposed delta against the page bounds (inset by `margin`)
    // so no box in the drag set can be moved past an edge. Each box
    // contributes its own min/max dx (and dy), and the group as a whole
    // moves by the most restrictive of those — preserves relative positions.
    const margin = props.margin ?? 0;
    let minDx = -Infinity;
    let maxDx = Infinity;
    let minDy = -Infinity;
    let maxDy = Infinity;
    dragIndices.forEach((idx, i) => {
      const img = images.value[idx];
      if (!img) return;
      minDx = Math.max(minDx, margin - dragOriginals[i].x);
      maxDx = Math.min(maxDx, props.width - margin - img.width - dragOriginals[i].x);
      minDy = Math.max(minDy, margin - dragOriginals[i].y);
      maxDy = Math.min(maxDy, props.height - margin - img.height - dragOriginals[i].y);
    });
    const clampedDx = Math.min(Math.max(dx, minDx), maxDx);
    const clampedDy = Math.min(Math.max(dy, minDy), maxDy);

    // Apply the (clamped) delta to every box in the drag snapshot so a
    // group of selected boxes moves rigidly together.
    dragIndices.forEach((idx, i) => {
      const img = images.value[idx];
      if (!img) return;
      img.coordX = dragOriginals[i].x + clampedDx;
      img.coordY = dragOriginals[i].y + clampedDy;
    });
    // Clear before redrawing so the previous frame's rectangles don't trail.
    drawingCanvas.value
      .getContext("2d")
      .clearRect(0, 0, drawingCanvas.value.width, drawingCanvas.value.height);
    redraw(false);
  }
}

function stopDraw(event: MouseEvent) {
  if (props.disableDrawing) return;

  // End of a resize — commit if anything actually moved.
  if (resizeHandle !== null) {
    const img = resizeBoxIndex !== null ? images.value[resizeBoxIndex] : null;
    const moved =
      img &&
      resizeOriginal &&
      (img.coordX !== resizeOriginal.coordX ||
        img.coordY !== resizeOriginal.coordY ||
        img.width !== resizeOriginal.width ||
        img.height !== resizeOriginal.height);
    resizeHandle = null;
    resizeBoxIndex = null;
    resizeOriginal = null;
    if (moved) {
      emit("update:images", [...images.value]);
      redraw(false);
    }
    return;
  }

  if (drawing) {
    const end = clampToBounds(event.offsetX, event.offsetY);
    const newImage: CanvasImage = {
      coordX: startingPositionX,
      coordY: startingPositionY,
      width: end.x - startingPositionX,
      height: end.y - startingPositionY,
    };

    if (newImage.width > 0 && newImage.height > 0) {
      images.value.push(newImage);
      stoppedDrawing.value = true;
    }
    redraw(true);
    setContext();
    drawing = false;
    currentImageIndex = images.value.length - 1;
    // ENG-20436 don't reset stoppedDrawing — picker just opened
    return;
  }

  // ENG-20693 End of a marquee — every box whose rect intersects the marquee
  // joins the current selection. Sub-DRAG_THRESHOLD marquees (tiny accidental
  // drags) are treated as plain clicks: clear the selection instead.
  if (marqueeActive) {
    const x = Math.min(startingPositionX, marqueeEndX);
    const y = Math.min(startingPositionY, marqueeEndY);
    const w = Math.abs(marqueeEndX - startingPositionX);
    const h = Math.abs(marqueeEndY - startingPositionY);
    marqueeActive = false;
    if (w <= DRAG_THRESHOLD_PX && h <= DRAG_THRESHOLD_PX) {
      clearSelection();
      return;
    }
    const next = new Set(selectedIndices.value);
    images.value.forEach((img, idx) => {
      const intersects =
        img.coordX < x + w &&
        img.coordX + img.width > x &&
        img.coordY < y + h &&
        img.coordY + img.height > y;
      if (intersects) next.add(idx);
    });
    selectedIndices.value = next;
    emitSelection();
    redraw(false);
    return;
  }

  if (dragCandidateIndex !== null) {
    const target = images.value[dragCandidateIndex];
    const wasDrag = dragging;
    dragCandidateIndex = null;
    dragging = false;
    dragIndices = [];
    dragOriginals = [];

    if (wasDrag && target) {
      // Commit the move — emit the full updated array up to the parent.
      emit("update:images", [...images.value]);
      redraw(false);
    }
    // Plain click (no drag) just updates selection (handled in startDraw).
    // Renaming is on dblclick; alt-drag duplicates.
  }
}

function redraw(performAction: boolean) {
  // Faint dashed guide showing the page-margin safe zone (only when > 0).
  if (drawingCanvas.value) {
    const margin = props.margin ?? 0;
    if (margin > 0) {
      const ctx = drawingCanvas.value.getContext("2d");
      ctx.save();
      ctx.strokeStyle = "rgba(0, 0, 0, 0.25)";
      ctx.lineWidth = 1;
      ctx.setLineDash([4, 4]);
      ctx.strokeRect(margin, margin, props.width - margin * 2, props.height - margin * 2);
      ctx.restore();
    }
  }
  let latestImage: CanvasImage = null;
  images.value.forEach((image: CanvasImage, idx: number) => {
    if (drawingCanvas.value) {
      const rect: CanvasRenderingContext2D = drawingCanvas.value.getContext("2d");
      rect.fillStyle = "rgba(255, 179, 71, 0.8)";
      rect.fillRect(image.coordX, image.coordY, image.width, image.height);

      if (image.text) {
        rect.fillStyle = "black";
        if (image.isCheckbox === false) {
          rect.font = "8pt monospace";
          rect.textBaseline = "alphabetic";
          rect.fillText(image.text, image.coordX + 5, image.coordY + 10, image.width);
        } else if (image.isCheckbox === true) {
          rect.font = "16pt monospace";
          rect.textBaseline = "hanging";
          rect.fillText("✓", image.coordX, image.coordY, image.width);
        }
      }

      if (selectedIndices.value.has(idx)) {
        rect.strokeStyle = "#0066ff";
        rect.lineWidth = 2;
        rect.strokeRect(image.coordX, image.coordY, image.width, image.height);
      }

      latestImage = image;
    }
  });

  // Resize handles — only rendered when exactly one box is selected, since
  // multi-box resize isn't implemented and showing handles on every selected
  // box would falsely advertise the capability.
  if (selectedIndices.value.size === 1 && drawingCanvas.value) {
    const idx = selectedIndices.value.values().next().value as number;
    const image = images.value[idx];
    if (image) {
      const ctx = drawingCanvas.value.getContext("2d");
      const s = RESIZE_HANDLE_PX;
      const cx = image.coordX + image.width / 2;
      const cy = image.coordY + image.height / 2;
      const positions: [number, number][] = [
        [image.coordX, image.coordY],
        [cx, image.coordY],
        [image.coordX + image.width, image.coordY],
        [image.coordX + image.width, cy],
        [image.coordX + image.width, image.coordY + image.height],
        [cx, image.coordY + image.height],
        [image.coordX, image.coordY + image.height],
        [image.coordX, cy],
      ];
      ctx.fillStyle = "#0066ff";
      ctx.strokeStyle = "white";
      ctx.lineWidth = 1;
      for (const [hx, hy] of positions) {
        ctx.fillRect(hx - s / 2, hy - s / 2, s, s);
        ctx.strokeRect(hx - s / 2, hy - s / 2, s, s);
      }
    }
  }

  if (performAction && stoppedDrawing.value) {
    focusMenu(latestImage);
  }
}

function onEnterKeydown(event: TextAreaKeyboardEvent) {
  event.preventDefault();

  if (stoppedDrawing.value) {
    if (filteredActions.value.length > 0) {
      selectVariable(filteredActions.value[focusedActionIndex.value]);
      return;
    } else {
      customVariable();
      return;
    }
  }

  // Get values before and after the current cursor selection position
  const selectionStart = event.target.selectionStart;
  const selectionEnd = event.target.selectionEnd;

  let leftOfSelection = null;
  if (selectionStart !== 0) leftOfSelection = inputValue.value.slice(0, selectionStart);

  let rightOfSelection = null;
  if (selectionEnd !== event.target.value.length)
    rightOfSelection = inputValue.value.slice(selectionEnd);

  emit("enterKeydown", <CursorValues>{
    leftOfSelection,
    rightOfSelection,
  });
}

function handleExit() {
  stoppedDrawing.value = false;
  hideMenu();

  if (currentImageIndex) {
    tidyImageAndInput();
  }
}

function handleKeydown(event: TextAreaKeyboardEvent) {
  switch (event.key) {
    case Key.ArrowDown: {
      event.preventDefault();
      menuOpen.value = true;
      if (menuDisplayVariables.value && refActionMenuItems.value.length > 0) {
        moveMenuFocus(Direction.DOWN, focusedActionIndex, refActionMenuItems);
      }
      break;
    }
    case Key.ArrowUp: {
      event.preventDefault();
      menuOpen.value = true;
      if (menuDisplayVariables.value && refActionMenuItems.value.length > 0) {
        moveMenuFocus(Direction.UP, focusedActionIndex, refActionMenuItems);
      }
      break;
    }
    case Key.Escape: {
      break;
    }
    case Key.Enter: {
      break;
    }
    default: {
      focusedActionIndex.value = 0;
    }
  }
}

function moveMenuFocus(
  direction: Direction,
  focusIndex: Ref,
  refList: Ref<InstanceType<typeof MItem>[]>,
) {
  if (direction === Direction.UP) {
    if (focusIndex.value - 1 >= 0) {
      focusIndex.value = focusIndex.value - 1;
    } else {
      focusIndex.value = refList.value.length - 1;
    }
  }

  if (direction === Direction.DOWN) {
    if (focusIndex.value + 1 <= refList.value.length - 1) {
      focusIndex.value = focusIndex.value + 1;
    } else {
      focusIndex.value = 0;
    }
  }

  nextTick(() => {
    const refFocusedItem = refList.value[focusIndex.value];
    refFocusedItem.$el.scrollIntoView();
  });
}

function tidyImageAndInput() {
  if (!images.value[currentImageIndex]?.id) {
    delete images.value[currentImageIndex];
    currentImageIndex = null;
    textAreaEl.value?.blur();
    inputValue.value = "";
    hideMenu();
    requestAnimationFrame(() => {
      redraw(false);
    });
  }
}

function hideMenu() {
  inputValue.value = "";
  stoppedDrawing.value = false;
  menuOpen.value = false;
  canvasTextAreaDisplay.value = "none";
  textLeftPosition.value = `-100px`;
  textTopPosition.value = `-100px`;
}

function focusMenu(image: CanvasImage) {
  if (!textAreaEl.value) return;
  // ENG-20436 position before display, focus on nextTick so display:initial has applied
  textLeftPosition.value = `${image.coordX}px`;
  textTopPosition.value = `${image.coordY + image.height}px`;
  canvasTextAreaDisplay.value = "initial";
  menuOpen.value = true;
  nextTick(() => {
    textAreaEl.value?.focus();
  });
}

function selectVariable(variable: VariableOption) {
  const image = images.value[currentImageIndex];
  if (image) {
    openModal(props.actionModalUrl, {
      params: {
        variable: variable.value,
        xPosition: image.coordX,
        yPosition: image.coordY,
        height: image.height,
        width: image.width,
      },
    });
    hideMenu();
  }
}

function customVariable() {
  const image = images.value[currentImageIndex];
  if (image) {
    openModal(props.actionModalUrl, {
      params: {
        variable: inputValue.value,
        xPosition: image.coordX,
        yPosition: image.coordY,
        height: image.height,
        width: image.width,
      },
    });
    hideMenu();
  }
}
</script>

<template>
  <div>
    <canvas
      id="drawing-canvas"
      ref="drawingCanvas"
      :height="height"
      :width="width"
      @mousedown="startDraw"
      @mousemove="draw"
      @mouseup="stopDraw"
      @mouseleave="stopDraw"
      @dblclick="handleDblClick"
    />
    <div id="canvas-text-area">
      <textarea
        ref="textAreaEl"
        v-model="inputValue"
        placeholder="Template variables"
        rows="1"
        @keydown.enter="onEnterKeydown"
        @keydown.esc="handleExit"
        @keydown="handleKeydown"
      />
      <MMenu
        v-if="stoppedDrawing"
        :target="textAreaEl"
        :model-value="menuOpen"
        :scroll-target="false"
        :max-height="MENU_MAX_HEIGHT"
        persistent
        no-auto-open
        fit
        :style="{ width: MENU_WIDTH + 'px' }"
        role="listbox"
        @update:model-value="menuOpen = $event"
      >
        <m-list v-if="filteredActions.length > 0" ref="list" class="scroll-area" dense tabindex="0">
          <m-item
            v-for="(variable, i) in filteredActions"
            :id="`${variable}-${i}`"
            :key="`${variable}-${i}`"
            ref="refActionMenuItems"
            v-close-popup
            clickable
            :focused="i === focusedActionIndex"
            tabindex="-1"
            :aria-selected="i === focusedActionIndex"
            role="option"
            @click="() => selectVariable(variable)"
            @keydown.enter="() => selectVariable(variable)"
          >
            <m-item-section>
              <m-item-label> {{ variable.label }} ({{ variable.value }}) </m-item-label>
            </m-item-section>
          </m-item>
        </m-list>
        <m-list v-else>
          <m-item
            ref="refActionMenuItems"
            class="label empty-text"
            clickable
            focused
            aria-selected
            @click="() => customVariable()"
          >
            <m-item-section>
              <m-item-label class="custom-variable-text">
                Custom variable: {{ inputValue }}
              </m-item-label>
            </m-item-section>
          </m-item>
        </m-list>
      </MMenu>
    </div>
  </div>
</template>

<style scoped lang="scss">
#drawing-canvas {
  background: rgb(20, 20, 20, 0.05);
}

#canvas-text-area {
  position: absolute;
  width: 200px;

  display: v-bind(canvasTextAreaDisplay);
  top: v-bind(textTopPosition);
  left: v-bind(textLeftPosition);
}

textarea {
  flex: 1 1 auto;
  border: 1px;
  width: 100%;
  resize: none;
  min-height: 26px;
  padding: 2px 8px;
  background: #ffffff;

  box-shadow:
    0px 4px 6px rgba(0, 0, 0, 0.06),
    0px 2px 4px rgba(0, 0, 0, 0.06);
  border-radius: 4px;
  overflow: hidden;

  &::placeholder {
    font-style: italic;
    color: #777;
  }

  &:focus {
    outline: none;
  }
}

.custom-variable-text {
  font-style: italic;
}
</style>
